import { IWizardCommonProps, Wizard, WizardPage, wizardServices } from "cayo.ui.wizards";
import React, { FC, useEffect, useReducer, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ConsentState } from "@cayo/shared/api/types/cayo-graph";
import { oClient } from "@cayo/shared/libs/oDataClient";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { retry } from "../../utils/retry";
import { getTrueKeys, grantConsentModern, validatePrincipalAccountName } from "./logic";
import { messages } from "./messages";
import { wizardStateReducer } from "./reducer";

import { Icon } from "@fluentui/react";
import styled from "styled-components";
import { addTenant, getEntraApp } from "./api";
import {
  AzureADConsentSubtitle,
  CreateApplication,
  FinishPrincipalPage,
  SelectServices,
} from "./subcomponents";
import { IAuthResponse } from "./types/IAuthResponse";

export const TenantWizard: FC<IWizardCommonProps> = (props) => {
  const intl = useIntl();
  const newTenantService = wizardServices.newTenantService;

  const [isFinished, setIsFinished] = useState(false);
  const [appName, setAppName] = useState("");
  const [wizardState, dispatchWizardState] = useReducer(wizardStateReducer, {
    usePasswordFlow: false,
    connectionIsRecommended: true,
    readonly: true,
    principalAccountName: "Cayosoft Guardian service account",
    systemTypes: {
      entra: true,
      exchangeOnline: false,
      intune: false,
      teams: false,
    },
  });

  const principalLimitError = validatePrincipalAccountName(
    wizardState?.principalAccountName!,
    intl.formatMessage(messages.principalAccountError)
  );

  const { tenant, consentState, connectionIsRecommended, grantUrl } = wizardState;
  const [loadingText, setLoadingText] = useState<string | undefined>(undefined);
  const [consentCheck, setConsentCheck] = useState<{
    state: string;
    isChecking?: boolean;
  }>({
    state: intl.formatMessage(messages.Unknown),
  });

  useEffect(() => {
    setConsentCheck({
      state: intl.formatMessage(messages.Unknown),
      isChecking: undefined,
    });
    dispatchWizardState({
      action: "setState",
      newState: {
        grantUrl: undefined,
        consentState: undefined,
        password: "",
        account: "",
        readonly: true,
      },
    });
  }, [connectionIsRecommended]);

  const checkConsent = (
    account: string,
    refreshToken?: string,
    password?: string,
    attempts?: number
  ) => {
    if (!refreshToken && !password) {
      throw new Error("No password or refresh token specified.");
    }

    setConsentCheck({
      state: intl.formatMessage(messages.checkConsent),
      isChecking: true,
    });

    retry(
      () =>
        newTenantService.checkConsentModern(account!, refreshToken, password).then((response) => {
          const newConsentState = response as ConsentState;
          if (newConsentState !== "granted" && newConsentState !== "invalidCredentials") {
            return Promise.reject(newConsentState);
          }

          return newConsentState;
        }),
      attempts || 30,
      2000
    )
      .then((response) => {
        const newConsentState = response as ConsentState;

        if (newConsentState === "invalidCredentials") {
          throw Error(newConsentState);
        }

        setConsentCheck({ state: newConsentState, isChecking: false });

        dispatchWizardState({
          action: "setConsentState",
          newState: { consentState: newConsentState },
        });
      })
      .catch((e) => {
        let state = e as ConsentState;
        if (
          !(
            state === "granted" ||
            state === "invalidCredentials" ||
            state === "notChecked" ||
            state === "notGranted" ||
            state === "partiallyGranted"
          )
        ) {
          dispatchWizardState({
            action: "setConsentError",
            newState: {
              consentError: e?.message || e?.toString() || "Unknown error",
            },
          });

          state = "notGranted";
        }

        setConsentCheck({ state, isChecking: false });

        throw e;
      });
  };

  const exchangeAuthCode = (auth: IAuthResponse) => {
    if (!auth.code) {
      dispatchWizardState({
        action: "setConsentError",
        newState: { consentError: auth.error_description },
      });

      return;
    }

    setLoadingText(intl.formatMessage(messages.getConsent));

    newTenantService
      .exchangeAuthCode(auth.code!, wizardState.account!)
      .then((refreshToken) => {
        dispatchWizardState({
          action: "setAzureADRefreshToken",
          newState: { refreshToken },
        });
        setLoadingText(undefined);

        return checkConsent(wizardState.account!, refreshToken);
      })
      .catch((e) => {
        setLoadingText(undefined);
        dispatchWizardState({
          action: "setConsentError",
          newState: { consentError: e },
        });
        throw e;
      });
  };

  const grant = (account: string, password?: string) => {
    setLoadingText(intl.formatMessage(messages.tenantRegistration));

    return newTenantService
      .grant(account!, endpoints.redirectUri, password)
      .then((response) => {
        dispatchWizardState({
          action: "setGrantUrl",
          newState: { grantUrl: response.url },
        });

        if (password) {
          return checkConsent(account, undefined, password, 1);
        }
      })
      .catch((e) => {
        setLoadingText(undefined);
        dispatchWizardState({
          action: "setTenantError",
          newState: { tenantError: e },
        });
        throw e;
      })
      .then(() => setLoadingText(undefined));
  };

  useEffect(() => {
    grant("");
  }, []);

  useEffect(() => {
    grant("");
  }, []);

  return (
    <Wizard
      loadingText={loadingText}
      title={intl.formatMessage(messages.wizardTitle)}
      isFinished={isFinished}
      activePage={0}
      data={wizardState}
      closeConfirmationMessage={intl.formatMessage(messages.closeConfirmation)}
      {...props}
    >
      <WizardPage
        isHidden={!wizardState.connectionIsRecommended}
        isInputValid={
          consentState === "granted" &&
          !principalLimitError &&
          (!wizardState.connectionIsRecommended ||
            !!wizardState.principalAccountName?.trim().length)
        }
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(messages.createApplication)}
        stepName={intl.formatMessage(messages.createApplication)}
        subTitle={!wizardState.connectionIsRecommended ? <AzureADConsentSubtitle /> : ""}
      >
        <CreateApplication
          wizardState={wizardState}
          dispatchWizardState={dispatchWizardState!}
          consentState={consentCheck.state}
          isChecking={consentCheck.isChecking}
          isPrincipal={wizardState.connectionIsRecommended}
          principalLimitError={principalLimitError}
          readonly={wizardState.readonly}
          grantConsent={() => {
            dispatchWizardState({
              action: "setConsentError",
              newState: { consentError: undefined },
            });

            dispatchWizardState({
              action: "setCreateError",
              newState: { createError: undefined },
            });

            grantUrl &&
              grantConsentModern(
                grantUrl!,
                wizardState.usePasswordFlow
                  ? () => checkConsent(wizardState.account!, undefined, wizardState.password)
                  : exchangeAuthCode
              );
          }}
        />
      </WizardPage>

      <WizardPage
        isHidden={wizardState.connectionIsRecommended}
        isInputValid={
          consentState === "granted" &&
          (!wizardState.connectionIsRecommended ||
            !!wizardState.principalAccountName?.trim().length)
        }
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(messages.consentStep)}
        stepName={intl.formatMessage(messages.consentStep)}
        subTitle={!wizardState.connectionIsRecommended ? <AzureADConsentSubtitle /> : ""}
      >
        <CreateApplication
          wizardState={wizardState}
          dispatchWizardState={dispatchWizardState!}
          consentState={consentCheck.state}
          isChecking={consentCheck.isChecking}
          isPrincipal={wizardState.connectionIsRecommended}
          readonly={wizardState.readonly}
          grantConsent={() => {
            dispatchWizardState({
              action: "setConsentError",
              newState: { consentError: undefined },
            });

            dispatchWizardState({
              action: "setCreateError",
              newState: { createError: undefined },
            });

            grantUrl &&
              grantConsentModern(
                grantUrl!,
                wizardState.usePasswordFlow
                  ? () => checkConsent(wizardState.account!, undefined, wizardState.password)
                  : exchangeAuthCode
              );
          }}
        />
      </WizardPage>

      <WizardPage
        title={intl.formatMessage(messages.selectService365)}
        stepName={intl.formatMessage(messages.selectService365)}
        subTitle={""}
        isInputValid={consentState === "granted"}
        onNext={async (data) => {
          const payload = {
            accountName: data.account,
            accountPassword: data.usePasswordFlow ? data.password : undefined,
            accountToken: data.refreshToken,
            servicePrincipalName: data.connectionIsRecommended
              ? data.principalAccountName
              : undefined,
            credentialUsage: !data.connectionIsRecommended
              ? "userAccount"
              : !data.readonly
                ? "applicationAdmin"
                : "application",
            configureChannels: false,
            systemTypes: getTrueKeys(data.systemTypes),
          };

          let tenant: any;

          await addTenant(
            payload,
            (createdTenant) => {
              tenant = createdTenant;
            },
            (error) => {
              dispatchWizardState({
                action: "setCreateError",
                newState: { createError: error },
              });
            }
          );

          dispatchWizardState({
            action: "setTenant",
            newState: { tenant },
          });

          const [jobs, entraAppRes]: [jobs: any, entraAppRes: any] = await Promise.all([
            oClient(endpoints.publicUrl)
              .get(`v0/configuration/managedTenants/${tenant.id}/getChangeMonitoringJobs`)
              .query()
              .catch(() => {
                return [];
              }),

            getEntraApp(tenant.objectPath).catch(() => {
              return [];
            }),
          ]);

          dispatchWizardState({
            action: "setPredefinedJobs",
            newState: { predefinedJobs: jobs },
          });

          const entraApp = entraAppRes?.[0];
          const applicationName = entraApp?.name || entraApp?.principalAccountName;

          setAppName(applicationName);
          setIsFinished(true);
        }}
      >
        <SelectServices wizardState={wizardState} dispatchWizardState={dispatchWizardState!} />
      </WizardPage>

      <WizardPage
        title={
          <StyledMessageWrapper>
            {consentState === "granted" && <StyledIcon iconName={"CompletedSolid"} />}

            <FormattedMessage
              id="new-managed-tenant-wizard.finish..principal.title"
              defaultMessage="Added {tenant}"
              values={{ tenant: (tenant && tenant.name) || "" }}
            />
          </StyledMessageWrapper>
        }
        stepName={intl.formatMessage(messages.finishStep)}
        subTitle={""}
      >
        <FinishPrincipalPage
          wizardState={wizardState}
          appName={appName}
          isPrincipal={wizardState.connectionIsRecommended!}
        />
      </WizardPage>
    </Wizard>
  );
};

export const StyledMessageWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const StyledIcon = styled(Icon)`
  color: ${(props) => props.theme.cayoTheme.brandColors.success};
  padding-right: 10px;
`;
